// Perception (§10.1). A ring of observed body states, TUNE.bot.perceptionTicks deep, fed every tick
// by the BotManager. A bot sees other beans as they were `reactionTicks` ago, extrapolated by their
// velocity for half the delay; its own bean is always current. Every brain reads the same ring at
// its own delay, which is equivalent to one ring per bot (docs/decisions/WP4.md) and costs 1/8th.
// Rows are keyed by round tick, so callers that replay ticks (client prediction) just overwrite.
import { DT, MAX_BEANS, TUNE } from '../constants';
import { EV } from '../events';
import { BS } from '../ids';
import { hasBody } from '../sim/bean';
import type { World } from '../types';
import { BOT_TUNE } from './tuning';

/** What a bot believes about one bean. */
export interface Seen {
  x: number; y: number; vx: number; vy: number; z: number;
  state: BS;
  facing: number;
  /** Ticks the bean had spent in `state` when observed. */
  st: number;
  /** The bean had a body (in play, not falling, not a ghost) when observed. */
  body: boolean;
  /** Round tick of the observation used. */
  tick: number;
}

export function createSeen(): Seen {
  return { x: 0, y: 0, vx: 0, vy: 0, z: 0, state: BS.NORMAL, facing: 0, st: 0, body: false, tick: 0 };
}

const NONE = -2147483648;

export class Perception {
  readonly depth = TUNE.bot.perceptionTicks + 2;
  private readonly rowTick = new Int32Array(this.depth).fill(NONE);
  private readonly x = new Float64Array(this.depth * MAX_BEANS);
  private readonly y = new Float64Array(this.depth * MAX_BEANS);
  private readonly vx = new Float64Array(this.depth * MAX_BEANS);
  private readonly vy = new Float64Array(this.depth * MAX_BEANS);
  private readonly z = new Float64Array(this.depth * MAX_BEANS);
  private readonly facing = new Float64Array(this.depth * MAX_BEANS);
  private readonly state = new Uint8Array(this.depth * MAX_BEANS);
  private readonly st = new Int32Array(this.depth * MAX_BEANS);
  private readonly body = new Uint8Array(this.depth * MAX_BEANS);
  /** Round tick each bean last started a lunge (from observed state changes). */
  readonly lungeTick = new Int32Array(MAX_BEANS).fill(NONE);
  /** Round tick each bean last started a glove windup. */
  readonly windupTick = new Int32Array(MAX_BEANS).fill(NONE);
  /** Round tick each bean last landed a hit (HIT events, seen and heard by everyone). */
  readonly hitTick = new Int32Array(MAX_BEANS).fill(NONE);
  /** Last recorded round tick. */
  lastTick = NONE;

  clear(): void {
    this.rowTick.fill(NONE);
    this.lungeTick.fill(NONE);
    this.windupTick.fill(NONE);
    this.hitTick.fill(NONE);
    this.lastTick = NONE;
  }

  private row(tick: number): number {
    const r = tick % this.depth;
    return r < 0 ? r + this.depth : r;
  }

  /** Records the world as it is now (after the step of `w.tick`). */
  record(w: World): void {
    const r = this.row(w.tick);
    const base = r * MAX_BEANS;
    const prevRow = this.rowTick[this.row(w.tick - 1)] === w.tick - 1 ? this.row(w.tick - 1) * MAX_BEANS : -1;
    for (let s = 0; s < MAX_BEANS; s++) {
      const b = w.beans[s];
      const k = base + s;
      this.x[k] = b.pos.x;
      this.y[k] = b.pos.y;
      this.vx[k] = b.vel.x;
      this.vy[k] = b.vel.y;
      this.z[k] = b.z;
      this.facing[k] = b.facing;
      this.state[k] = b.state;
      this.st[k] = b.st;
      this.body[k] = hasBody(b) ? 1 : 0;
      const prevState = prevRow >= 0 ? this.state[prevRow + s] : -1;
      if (b.state === BS.LUNGE && (prevState !== BS.LUNGE || b.st === 0)) this.lungeTick[s] = w.tick - b.st;
      if (b.state === BS.PUNCH_WINDUP && prevState !== BS.PUNCH_WINDUP) this.windupTick[s] = w.tick - b.st;
    }
    for (const ev of w.events) if (ev.type === EV.HIT && ev.a < MAX_BEANS) this.hitTick[ev.a] = w.tick;
    this.rowTick[r] = w.tick;
    this.lastTick = w.tick;
  }

  /** Bean `slot`'s last lunge (as seen `delay` ticks late) found nobody: a whiff worth punishing. */
  whiffed(slot: number, tick: number, delay: number): boolean {
    const l = this.lungeTick[slot];
    return l !== NONE && l <= tick - delay && this.hitTick[slot] < l;
  }

  /** Records the current tick unless it is already in the ring. */
  ensure(w: World): void {
    if (this.rowTick[this.row(w.tick)] !== w.tick) this.record(w);
  }

  /**
   * Fills `out` with bean `slot` as seen `delay` ticks before `tick`, extrapolated by its velocity
   * for `extrapolateFrac` of the delay. Falls back to the newest older row when the exact one is
   * missing, and to the world itself when the ring is empty.
   */
  view(w: World, slot: number, tick: number, delay: number, out: Seen): Seen {
    const d = Math.max(0, Math.min(this.depth - 2, Math.round(delay)));
    let t = tick - d;
    let r = -1;
    for (let tries = 0; tries < this.depth; tries++, t--) {
      const rr = this.row(t);
      if (this.rowTick[rr] === t) {
        r = rr;
        break;
      }
    }
    if (r < 0) {
      const b = w.beans[slot];
      out.x = b.pos.x;
      out.y = b.pos.y;
      out.vx = b.vel.x;
      out.vy = b.vel.y;
      out.z = b.z;
      out.state = b.state;
      out.facing = b.facing;
      out.st = b.st;
      out.body = hasBody(b);
      out.tick = w.tick;
      return out;
    }
    const k = r * MAX_BEANS + slot;
    const age = (tick - t) * BOT_TUNE.perceive.extrapolateFrac * DT;
    out.vx = this.vx[k];
    out.vy = this.vy[k];
    out.x = this.x[k] + out.vx * age;
    out.y = this.y[k] + out.vy * age;
    out.z = this.z[k];
    out.state = this.state[k] as BS;
    out.facing = this.facing[k];
    out.st = this.st[k];
    out.body = this.body[k] === 1;
    out.tick = t;
    return out;
  }
}
